import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { User, Lock, CreditCard, Shield, ArrowLeft, Sparkles } from 'lucide-react';
import PersonalInfoForm from '../components/forms/PersonalInfoForm';
import PasswordChangeForm from '../components/forms/PasswordChangeForm';
import SubscriptionSection from '../components/sections/SubscriptionSection';
import IconSelector from '../components/profile/IconSelector';

const AccountSettings: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'personal' | 'icon' | 'security' | 'subscription'>('personal');

  if (!user) {
    return <div>Loading...</div>;
  }

  const tabs = [
    { id: 'personal' as const, name: 'Personal Info', icon: User },
    { id: 'icon' as const, name: 'Profile Icon', icon: Sparkles },
    { id: 'security' as const, name: 'Security', icon: Lock },
    { id: 'subscription' as const, name: 'Subscription', icon: CreditCard },
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="flex items-center space-x-2 text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
              >
                <ArrowLeft className="h-5 w-5" />
                <span className="text-sm font-medium">Back to Dashboard</span>
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Account Settings
                </h1>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Manage your account preferences and settings
                </p>
              </div>
            </div>
            {user.is_admin && (
              <div className="flex items-center space-x-2">
                <Shield className="h-5 w-5 text-red-500" />
                <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                  ADMIN
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
          {/* Tab Navigation */}
          <div className="border-b border-gray-200 dark:border-gray-700">
            <nav className="flex space-x-8 px-6" aria-label="Tabs">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 transition-colors ${
                      activeTab === tab.id
                        ? 'border-mango-500 text-mango-600 dark:text-mango-400'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                    }`}
                  >
                    <Icon className="h-5 w-5" />
                    <span>{tab.name}</span>
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'personal' && <PersonalInfoForm />}
            {activeTab === 'icon' && (
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Choose Your Profile Icon
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                  Select from unlocked icons or achieve milestones to unlock more!
                </p>
                <IconSelector />
              </div>
            )}
            {activeTab === 'security' && <PasswordChangeForm />}
            {activeTab === 'subscription' && <SubscriptionSection />}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountSettings;